import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {Button} from '@/components/ui/button/Button'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {ProjectMedicalDevicesFields} from '@/features/projects/components/project-medical-devices-fields/ProjectMedicalDevicesFields'
import {MedicalDevicesFormSchema, ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useStoreProjectMedicalDevices} from '@/features/projects/queries/useStoreProjectMedicalDevices'
import {MedicalDevice} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useFieldArray, useForm} from 'react-hook-form'

interface ProjectMedicalDevicesFormProps {
    projectId: string
    defaultValues?: ProjectFieldsSchema['devices']
    isDisabled: boolean
    isEditModeActive: boolean
    handleCancel: () => void
    handleEdit: () => void
}

export const ProjectMedicalDevicesForm = ({
    projectId,
    defaultValues,
    isDisabled,
    isEditModeActive,
    handleCancel,
    handleEdit
}: ProjectMedicalDevicesFormProps) => {
    const {t} = useTranslation()
    const form = useForm<MedicalDevicesFormSchema>({
        resolver: zodResolver(MedicalDevicesFormSchema),
        defaultValues: {
            devices: defaultValues?.length ? defaultValues : [{device: undefined}]
        }
    })

    const {fields, append, remove} = useFieldArray({
        control: form.control,
        name: 'devices'
    })

    const {mutate: storeProjectMedicalDevices, isPending} = useStoreProjectMedicalDevices({
        urlParams: {
            projectId
        },
        options: {
            onSuccess: async () => {
                await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT_MEDICAL_DEVICES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_MEDICAL_DEVICES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
                handleCancel()
            }
        }
    })

    const onSubmit = form.handleSubmit(
        data => storeProjectMedicalDevices(data),
        formErrors => console.error(formErrors)
    )

    return (
        <FormProvider {...form}>
            <DetailsFieldsSection
                variant="neutral"
                title={t('projects:medical_devices_card_title')}
                isDisabled={isDisabled}
                headingEndSlot={
                    <EditDetailsSectionButtons
                        isLoading={isPending}
                        isActive={isEditModeActive}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                        onConfirm={onSubmit}
                        isDisabled={!form.formState.isValid || !ObjectKeys(form.formState.dirtyFields).length}
                    />
                }
            >
                <Flexbox direction="column" gap={6} fullWidth>
                    {fields.map((field, index) => (
                        <ProjectMedicalDevicesFields
                            key={field.id}
                            index={index}
                            disabledDevices={fields.map(field => field.device)}
                            showTrashButton={fields.length > 1}
                            remove={remove}
                        />
                    ))}
                    {fields.length < 3 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => append({device: undefined as unknown as MedicalDevice['device']})}
                        >
                            <PlusIcon />
                            {t('projects:add_medical_device')}
                        </Button>
                    )}
                </Flexbox>
            </DetailsFieldsSection>
        </FormProvider>
    )
}
